import { useState, useEffect, useCallback } from 'react';
import { opsApi } from '@/services/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import {
  Wifi, Tag, CheckCircle, Package, ArrowRight, Loader2,
  QrCode, AlertCircle, Zap
} from 'lucide-react';

export default function DispatcherWorkflowPage() {
  const [step, setStep] = useState(1); // 1: Select Device, 2: Scan Labels, 3: Confirm
  const [devices, setDevices] = useState([]);
  const [selectedDevice, setSelectedDevice] = useState(null);
  const [labels, setLabels] = useState([]);
  const [selectedLabels, setSelectedLabels] = useState([]);
  const [invoiceRef, setInvoiceRef] = useState('');
  const [users, setUsers] = useState([]);
  const [assigneeId, setAssigneeId] = useState('');
  const [loading, setLoading] = useState(true);
  const [activating, setActivating] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    fetchInitialData();
     
  }, []);

  const fetchInitialData = async () => {
    try {
      setLoading(true);
      const [devRes, lblRes, usrRes] = await Promise.all([
        opsApi.getReadyDevices(),
        opsApi.getAvailableLabels(50),
        opsApi.getUsers(),
      ]);
      setDevices(devRes.data || []);
      setLabels(lblRes.data || []);
      setUsers((usrRes.data || []).filter((u) => u.status === 'active'));
    } catch (err) {
      console.error('Failed to load dispatcher data:', err);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const toggleLabel = (labelId) => {
    setSelectedLabels(prev =>
      prev.includes(labelId) ? prev.filter(id => id !== labelId) : [...prev, labelId]
    );
  };

  const handleActivate = async () => {
    if (!selectedDevice || selectedLabels.length === 0) return;
    try {
      setActivating(true);
      const res = await opsApi.dispatcherActivate({
        device_id: selectedDevice.id,
        label_ids: selectedLabels,
        invoice_ref: invoiceRef || null,
        assignee_user_id: assigneeId || null,
      });
      setResult(res.data);
      toast.success(res.data.message);
      setStep(4); // Success
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Activation failed');
    } finally {
      setActivating(false);
    }
  };

  const resetFlow = () => {
    setStep(1);
    setSelectedDevice(null);
    setSelectedLabels([]);
    setInvoiceRef('');
    setAssigneeId('');
    setResult(null);
    fetchInitialData();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-teal-500" />
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 space-y-6" data-testid="dispatcher-workflow-page">
      <div>
        <h1 className="text-2xl font-bold text-slate-900 dark:text-white">Dispatcher Activation</h1>
        <p className="text-slate-500 dark:text-slate-400 text-sm">Device-first, scan labels, one confirm — all labels activate atomically</p>
      </div>

      {/* Step Indicator */}
      <div className="flex items-center gap-2">
        {['Select Device', 'Scan Labels', 'Confirm', 'Done'].map((label, idx) => {
          const num = idx + 1;
          const isActive = step === num;
          const isDone = step > num;
          return (
            <div key={label} className="flex items-center gap-2">
              {idx > 0 && <ArrowRight className={cn("w-4 h-4", isDone ? "text-teal-500" : "text-slate-300 dark:text-slate-600")} />}
              <div className={cn(
                "flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium",
                isActive ? "bg-teal-600 text-white" :
                isDone ? "bg-teal-100 dark:bg-teal-900/30 text-teal-700 dark:text-teal-400" :
                "bg-slate-100 dark:bg-slate-800 text-slate-400"
              )}>
                {isDone ? <CheckCircle className="w-4 h-4" /> : <span>{num}</span>}
                <span className="hidden sm:inline">{label}</span>
              </div>
            </div>
          );
        })}
      </div>

      {/* Step 1: Device Selection */}
      {step === 1 && (
        <Card className="bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700">
          <CardHeader>
            <CardTitle className="text-slate-900 dark:text-white flex items-center gap-2">
              <Wifi className="w-5 h-5 text-teal-500" />Step 1 — Select Device
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-slate-500 dark:text-slate-400 text-sm mb-4">
              Rule 1: Device first, always. Scan device QR or select from fleet.
            </p>
            {devices.length === 0 ? (
              <div className="text-center py-8 text-slate-500">
                <AlertCircle className="w-10 h-10 mx-auto mb-3 opacity-40" />
                <p>No ready devices available. Check device health.</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {devices.map(d => (
                  <div
                    key={d.id}
                    onClick={() => { setSelectedDevice(d); setStep(2); }}
                    className={cn(
                      "border rounded-lg p-4 cursor-pointer transition-all hover:shadow-md",
                      "border-slate-200 dark:border-slate-700 hover:border-teal-500 dark:hover:border-teal-500"
                    )}
                    data-testid={`device-${d.device_uid}`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-mono font-bold text-slate-900 dark:text-white">{d.device_uid}</span>
                      <Badge className="bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400">{d.status}</Badge>
                    </div>
                    <div className="text-xs text-slate-500 space-y-1">
                      {d.current_temp && <p>Temp: {d.current_temp}°C</p>}
                      {d.battery_level && <p>Battery: {d.battery_level}%</p>}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Step 2: Scan Labels */}
      {step === 2 && (
        <Card className="bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700">
          <CardHeader>
            <CardTitle className="text-slate-900 dark:text-white flex items-center gap-2">
              <Tag className="w-5 h-5 text-teal-500" />Step 2 — Scan Passport Labels
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="bg-teal-50 dark:bg-teal-900/20 rounded-lg p-3 flex items-center gap-3">
              <Wifi className="w-5 h-5 text-teal-600" />
              <div>
                <p className="text-teal-800 dark:text-teal-300 font-medium text-sm">Device: {selectedDevice?.device_uid}</p>
                <p className="text-teal-600 dark:text-teal-400 text-xs">Telemetry streaming — place device in bag now</p>
              </div>
            </div>

            <p className="text-slate-500 dark:text-slate-400 text-sm">
              Rule 2: Purely scan — mapped — next. Select labels for each cold packet in this bag.
            </p>

            <div className="mb-3">
              <label className="text-sm text-slate-600 dark:text-slate-400 mb-1 block">Invoice Reference (optional)</label>
              <Input
                value={invoiceRef}
                onChange={(e) => setInvoiceRef(e.target.value)}
                placeholder="e.g. INV-2026-001"
                className="dark:bg-slate-900 dark:border-slate-700 dark:text-white"
                data-testid="invoice-ref-input"
              />
            </div>

            {labels.length === 0 ? (
              <div className="text-center py-6 text-slate-500">
                <QrCode className="w-8 h-8 mx-auto mb-2 opacity-40" />
                <p>No dormant labels available. Create a label batch first.</p>
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2 max-h-64 overflow-y-auto">
                {labels.map(l => {
                  const selected = selectedLabels.includes(l.id);
                  return (
                    <div
                      key={l.id}
                      onClick={() => toggleLabel(l.id)}
                      className={cn(
                        "border rounded-lg p-3 cursor-pointer transition-all text-center text-sm",
                        selected
                          ? "border-teal-500 bg-teal-50 dark:bg-teal-900/30 ring-2 ring-teal-500"
                          : "border-slate-200 dark:border-slate-700 hover:border-teal-300"
                      )}
                      data-testid={`label-${l.label_uid}`}
                    >
                      <QrCode className={cn("w-5 h-5 mx-auto mb-1", selected ? "text-teal-600" : "text-slate-400")} />
                      <p className={cn("font-mono text-xs", selected ? "text-teal-700 dark:text-teal-300 font-bold" : "text-slate-600 dark:text-slate-400")}>{l.label_uid}</p>
                      {selected && <CheckCircle className="w-4 h-4 text-teal-500 mx-auto mt-1" />}
                    </div>
                  );
                })}
              </div>
            )}

            <div className="flex items-center justify-between pt-4 border-t border-slate-200 dark:border-slate-700">
              <Button variant="outline" onClick={() => { setStep(1); setSelectedDevice(null); }} className="dark:border-slate-600 dark:text-slate-300">
                Back
              </Button>
              <div className="flex items-center gap-3">
                <span className="text-sm text-slate-500">{selectedLabels.length} labels selected</span>
                <Button
                  onClick={() => setStep(3)}
                  disabled={selectedLabels.length === 0}
                  className="bg-teal-600 hover:bg-teal-700"
                  data-testid="proceed-to-confirm-btn"
                >
                  Proceed <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Step 3: Confirm */}
      {step === 3 && (
        <Card className="bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700">
          <CardHeader>
            <CardTitle className="text-slate-900 dark:text-white flex items-center gap-2">
              <Zap className="w-5 h-5 text-teal-500" />Step 3 — Confirm Activation
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-slate-500 dark:text-slate-400 text-sm">
              Rule 3: One Confirm activates everything. Single tap — all labels go ACTIVE atomically.
            </p>

            <div className="bg-slate-50 dark:bg-slate-900 rounded-lg p-4 space-y-3">
              <div className="flex justify-between text-sm">
                <span className="text-slate-500 dark:text-slate-400">Device</span>
                <span className="font-mono font-bold text-slate-900 dark:text-white">{selectedDevice?.device_uid}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-slate-500 dark:text-slate-400">Labels to activate</span>
                <span className="font-bold text-teal-600 dark:text-teal-400">{selectedLabels.length}</span>
              </div>
              {invoiceRef && (
                <div className="flex justify-between text-sm">
                  <span className="text-slate-500 dark:text-slate-400">Invoice</span>
                  <span className="text-slate-900 dark:text-white">{invoiceRef}</span>
                </div>
              )}
            </div>

            {/* Assign to a Supplyman / delivery agent */}
            <div className="space-y-2">
              <label className="text-sm text-slate-600 dark:text-slate-400">Assign to Supplyman (optional)</label>
              <select
                value={assigneeId}
                onChange={(e) => setAssigneeId(e.target.value)}
                className="w-full rounded-md border bg-white dark:bg-slate-900 border-slate-300 dark:border-slate-700 text-slate-900 dark:text-white px-3 py-2 text-sm"
                data-testid="assignee-select"
              >
                <option value="">— Not assigning yet (I'll dispatch myself) —</option>
                {users.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.full_name} · {u.email}
                  </option>
                ))}
              </select>
              {assigneeId && (
                <p className="text-xs text-slate-500 dark:text-slate-400">
                  Work order will be created in <strong>ASSIGNED</strong> state — supplyman accepts from their Work Orders page.
                </p>
              )}
            </div>

            <div className="flex items-center justify-between pt-4">
              <Button variant="outline" onClick={() => setStep(2)} className="dark:border-slate-600 dark:text-slate-300">
                Back
              </Button>
              <Button
                onClick={handleActivate}
                disabled={activating}
                className="bg-teal-600 hover:bg-teal-700 h-12 px-8"
                data-testid="confirm-activate-btn"
              >
                {activating ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Activating...</>
                ) : (
                  <><Zap className="w-5 h-5 mr-2" />Confirm — Activate All</>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Step 4: Success */}
      {step === 4 && result && (
        <Card className="bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700">
          <CardContent className="p-8 text-center space-y-4">
            <CheckCircle className="w-16 h-16 text-green-500 mx-auto" />
            <h2 className="text-xl font-bold text-slate-900 dark:text-white">Activation Complete</h2>
            <p className="text-slate-500 dark:text-slate-400">
              {result.labels_activated} labels activated on device {result.device_uid}
            </p>
            <div className="bg-slate-50 dark:bg-slate-900 rounded-lg p-4 space-y-2 text-sm text-left max-w-md mx-auto">
              <div className="flex justify-between">
                <span className="text-slate-500">Work Order</span>
                <span className="font-mono text-slate-900 dark:text-white">{result.wo_number}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500">Passport</span>
                <span className="font-mono text-teal-600 text-xs">{result.passport_uuid?.slice(0, 12)}...</span>
              </div>
            </div>
            <Button onClick={resetFlow} className="bg-teal-600 hover:bg-teal-700" data-testid="new-bag-btn">
              <Package className="w-4 h-4 mr-2" />Next Bag
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
